import React, { useEffect, useState } from 'react';

import Modal from 'antd/lib/modal';
import styled from 'styled-components';

import { sendEmailNotification } from 'apis/CommunityAPI';
import { useRootContext } from 'components/layout/RootContext';
import Button from 'components/lib/Button';
import InputMultipleEmails from 'components/lib/InputMultipleEmails';
import { isManagedBySuperuser } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import { getTabDataOrDefault, useShareViaEmailContext } from './ShareViaEmailContext';
import { useCommunityViewContext } from '../CommunityViewContext';

const StyledModal = styled(Modal)`
  display: flex;
  justify-content: center;

  .ant-modal-content {
    width: 540px;
    border-radius: 0;
    padding: 28px 30px 20px 30px;
  }

  .ant-modal-title {
    font-size: 20px;
    font-family: var(--font-bold);
  }
`;

const Content = styled.div`
  margin: 0 -30px;
  padding: 5px 30px 30px 30px;
  border-bottom: 1px solid var(--line-gray);
`;

const Description = styled.div`
  color: var(--text-secondary);
  font-family: var(--font-regular);
`;

const EmailRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 10px;
`;

const InputWrapper = styled.div`
  flex: 1;
`;

const FormGroup = styled.div`
  display: flex;
  align-items: center; /* vertically align label + input */
  padding-top: 20px;
  gap: 12px; /* space between label and input */
`;

const Label = styled.div`
  font-family: var(--font-bold);
  white-space: nowrap;
  flex-shrink: 0;
`;

const StyledButton = styled(Button)`
  width: 120px;
  margin-top: 8px;
`;

type Props = {
  showModal: boolean;
  onConfirm: () => void;
  onClose: () => void;
};

const SendTestEmailNotificationModal = ({ showModal, onConfirm, onClose }: Props) => {
  const { showMessage } = useRootContext();
  const { notificationsTabData, notificationType } = useShareViaEmailContext();

  // Determine current emails and selectedUsers based on active tab
  const { communityId } = useCommunityViewContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const [testEmails, setTestEmails] = useState<string[]>([]);

  const currentData = getTabDataOrDefault(notificationsTabData?.[notificationType]);

  useEffect(() => {
    if (isManagedBySuperuser(currentUser) && currentUser?.manager?.email) {
      setTestEmails([currentUser.manager.email]);
    }
  }, [currentUser, showModal]);

  const handleSend = async () => {
    const emails = testEmails.map((item: string) => item.trim());
    const recipients = emails.map((email) => ({ id: null, email }));

    await sendEmailNotification(communityId, notificationType, {
      recipients,
      competitor_ids: currentData.selectedCompetitors,
    })
      .then(() => {
        showMessage('success', 'Email notification sent succesfully.');
      })
      .catch(() => {
        showMessage('error', 'Trouble sending email notification.');
      });

    onConfirm();
  };

  return (
    <StyledModal
      open={Boolean(showModal)}
      centered
      title={'Send Test Email'}
      onCancel={onClose}
      footer={[
        <StyledButton key="confirm" type="primary" onClick={handleSend} disabled={testEmails.length == 0}>
          Send
        </StyledButton>,
        <StyledButton key="cancel" onClick={onClose}>
          Cancel
        </StyledButton>,
      ]}
    >
      <Content>
        <Description>
          This will send the selected email notification type to the address specified below, as a preview.
        </Description>
        <FormGroup>
          <Label>Email Address</Label>
          <EmailRow>
            <InputWrapper>
              <InputMultipleEmails emails={testEmails} setEmails={setTestEmails} placeholder={'Enter email address'} />
            </InputWrapper>
          </EmailRow>
        </FormGroup>
      </Content>
    </StyledModal>
  );
};

export default SendTestEmailNotificationModal;
